import React, { useState } from "react";
import { Helmet } from "react-helmet-async";

import Navbar from "../Components/NavbarComponent";
import FooterComponent from "../Components/FooterComponent";
import SideNavComponent from "../Components/SideNavComponent";
import MastheadAnnouncement from "../Components/MastheadAnnouncement";

import {
  ChatIcon,
  Chevron,
  CodeBlock,
  CodeCard,
  InsetNote,
  PinIcon,
  QuickReference,
  SectionRule,
  ServerIcon,
  Troubleshooting,
  UserIcon,
} from "./MCP/McpParts";

import {
  ToolEntry,
  countTools,
  isStackedGroup,
} from "./MCP/McpToolTable";

import {
  ACCESS_CURL,
  ACCESS_RESPONSE,
  ACCESS_TOKEN_ONE_LINER,
  CLAUDE_CODE_ADD,
  CONFIG_CARDS,
  ONEMAP_REGISTER_CONFIRM_URL,
  ONEMAP_REGISTER_URL,
  REFRESH_CURL,
  REFRESH_RESPONSE,
  SERVER_ADDRESS_CARD,
  TOOLS_LIST_CURL,
} from "./MCP/McpEndpoint";

import { TOOL_GROUPS } from "./MCP/McpTools";
import { QUICK_REFERENCE, TROUBLESHOOTING } from "./MCP/McpReference";

const CLAUDE_DESKTOP_URL = "https://claude.com/download";
const CLAUDE_CODE_URL = "https://claude.com/product/claude-code";
const KIRO_URL = "https://kiro.dev/download";

const TOOL_COUNT = countTools(TOOL_GROUPS);

const ExternalLink = ({ href, children }) => (
  <a href={href} target="_blank" rel="noopener noreferrer">
    {children}
  </a>
);

const MCP = () => {
  const [isCollapsed, setIsCollapsed] = useState(true);
  const [showCurlCheck, setShowCurlCheck] = useState(false);

  const handleCollapse = () => {
    setIsCollapsed(!isCollapsed);

    if (isCollapsed) {
      window.scrollTo(0, 0);
    }
  };

  const handleAnchorClick = (event, id) => {
    event.preventDefault();
    const target = document.getElementById(id);
    if (target) {
      target.scrollIntoView({ behavior: "smooth" });
      const { pathname, search } = window.location;
      window.history.pushState(null, "", `${pathname}${search}#${id}`);
    }
  };

  return (
    <>
      <Helmet>
        <title>Model Context Protocol (MCP) | OneMap</title>
        <meta
          name="description"
          content="Connect an AI assistant or agent to OneMap through the Model Context Protocol: get an access token, load the server in Claude Desktop, Claude Code or Kiro, and explore the available tools."
        />
      </Helmet>
      <MastheadAnnouncement />
      <Navbar />
      <div className="row gx-0 mb-5 position-relative">
        <div className="col-1 d-md-none d-inline">
          <button
            type="button"
            className="btn btn-primary d-md-none position-fixed sideBarBtn"
            onClick={handleCollapse}
            aria-expanded={!isCollapsed}
            aria-controls="mcp-sidenav"
          >
            Show Side Bar
          </button>
        </div>

        <div
          id="mcp-sidenav"
          className={`px-0 col-10 col-md-3 d-md-block ${
            isCollapsed ? "collapse" : ""
          }`}
        >
          <SideNavComponent />
        </div>

        <div className="col-12 col-md-9 px-0 px-md-3">
          <div className="om-mcp">
            <section className="om-mcp-block">
              <h1 className="om-mcp-h1" id="mcp">OneMap Model Context Protocol (MCP) Server (Beta)</h1>
              <p className="om-mcp-desc">
                Connect AI agents to Singapore&apos;s national map platform
                through Model Context Protocol tools for geocoding,  coordinate conversion, routing, nearby transit, thematic datasets, and population statistics.
              </p>
            </section>

            {/* ---------- 01 How MCP works ---------- */}
            <section id="mcp-quickstart" className="om-mcp-block">
              <h2 className="om-mcp-h2">How MCP works</h2>
              <p className="om-mcp-lede">
                The Model Context Protocol (MCP) is an open standard that enables AI applications
                to connect with external tools and data sources through a unified interface. With MCP,
                AI applications can access OneMap data in real time, allowing responses to be generated
                using current and authoritative information rather than relying solely on pre-trained knowledge.
              </p>

              {/* Request flow */}
              <ol className="om-mcp-flow" aria-label="How a request flows">
                <li className="om-mcp-flow-node">
                  <span className="om-mcp-icon-circle">
                    <UserIcon />
                  </span>
                  <strong>User</strong>
                  <span>Ask Question in Natural Language</span>
                </li>
                <li className="om-mcp-flow-node">
                  <span className="om-mcp-icon-circle">
                    <ChatIcon />
                  </span>
                  <strong>Agentic tool</strong>
                  <span className="om-mcp-flow-term">MCP client</span>
                  <span>Eg. Claude Desktop, Kiro</span>
                </li>
                <li className="om-mcp-flow-node is-highlight">
                  <span className="om-mcp-icon-circle">
                    <ServerIcon />
                  </span>
                  <strong>OneMap MCP server</strong>
                  <span className="om-mcp-flow-term">MCP server</span>
                  <span>{TOOL_COUNT} Geospatial Services & Tools</span>
                </li>
                <li className="om-mcp-flow-node">
                  <span className="om-mcp-icon-circle">
                    <PinIcon />
                  </span>
                  <strong>OneMap Services</strong>
                  <span>Singapore Authoritative Geospatial Data (change)</span>
                </li>
              </ol>

              {/* Client view and server view */}
              <div className="om-mcp-duo">
                <article className="om-mcp-mock">
                  <h3 className="om-mcp-mock-title">
                    What you see in your agentic tool
                  </h3>
                  <ol className="om-mcp-mock-steps">
                    <li>
                      <span className="om-mcp-step-dot" aria-hidden="true">1</span>
                      <span className="om-mcp-bubble om-mcp-bubble-user">
                        What's the nearest MRT to 1 North Buona Vista
                        Drive?
                      </span>
                    </li>
                    <li>
                      <span className="om-mcp-step-dot" aria-hidden="true">2</span>
                      <span>
                        Calls
                        <span className="om-mcp-toolcalls">
                          <mark className="om-mcp-chip">address___search</mark>
                          <span className="om-mcp-tc-arrow" aria-hidden="true">
                            &darr;
                          </span>
                          <mark className="om-mcp-chip">nearby___mrt_stations</mark>
                        </span>
                      </span>
                    </li>
                    <li>
                      <span className="om-mcp-step-dot" aria-hidden="true">3</span>
                      <span>Waits for the OneMap MCP server</span>
                    </li>
                    <li>
                      <span className="om-mcp-step-dot" aria-hidden="true">4</span>
                      <span className="om-mcp-bubble om-mcp-bubble-ai">
                        The nearest station is Buona Vista MRT (EW21), on North
                        Buona Vista Road.
                      </span>
                    </li>
                  </ol>
                </article>

                <div className="om-mcp-connector" aria-hidden="true">
                  <div className="om-mcp-connector-arrow">
                    <span className="om-mcp-arrow-glyph">&rarr;</span>
                    <span className="om-mcp-arrow-label">Request</span>
                  </div>
                  <div className="om-mcp-connector-arrow">
                    <span className="om-mcp-arrow-glyph">&larr;</span>
                    <span className="om-mcp-arrow-label">Response</span>
                  </div>
                </div>

                <article className="om-mcp-mock">
                  <h3 className="om-mcp-mock-title">
                    What happens on the OneMap MCP server
                  </h3>
                  <ol className="om-mcp-mock-steps">
                    <li>
                      <span className="om-mcp-step-dot" aria-hidden="true">1</span>
                      <span>Receives the request from your agentic tool</span>
                    </li>
                    <li>
                      <span className="om-mcp-step-dot" aria-hidden="true">2</span>
                      <span>
                        Runs <mark className="om-mcp-chip">address___search</mark>, then{" "}
                        <mark className="om-mcp-chip">nearby___mrt_stations</mark>
                      </span>
                    </li>
                    <li>
                      <span className="om-mcp-step-dot" aria-hidden="true">3</span>
                      <span>
                        Looks up the result in live OneMap data
                        <span className="om-mcp-toolcalls om-mcp-result">
                          <span className="om-mcp-result-label">Result returned</span>
                          <code>
                            {`{ "id": "EW21", "name": "BUONA VISTA MRT STATION", "lat": 1.3073, "lon": 103.7900, "road": "NORTH BUONA VISTA ROAD" }`}
                          </code>
                        </span>
                      </span>
                    </li>
                    <li>
                      <span className="om-mcp-step-dot" aria-hidden="true">4</span>
                      <span>Sends the result back to your agentic tool</span>
                    </li>
                  </ol>
                </article>
              </div>

              <div className="om-mcp-callout">
                <h3 className="om-mcp-callout-title">
                  How it works
                </h3>
                <p className="om-mcp-callout-body">
                  You will not interact with the OneMap tools directly. Instead, your AI
                  agent communicates with the OneMap MCP server and executes the required tools on your behalf.
                  To enable this, configure the server address and a valid access token using the instruction below.
                </p>
              </div>

              <h3 className="om-mcp-category">What you'll need</h3>
              <div className="om-mcp-need-row">
                <div className="om-mcp-need-chip">
                  <strong>OneMap account</strong>
                  <span>Step 1 sets it up</span>
                </div>
                <div className="om-mcp-need-chip">
                  <strong>An MCP-ready AI app</strong>
                  <span>
                    Download one:{" "}
                    <ExternalLink href={CLAUDE_DESKTOP_URL}>
                      Claude Desktop
                    </ExternalLink>{" "}
                    or <ExternalLink href={KIRO_URL}>Kiro</ExternalLink>. Sign
                    in after installing.
                  </span>
                </div>
                <div className="om-mcp-need-chip">
                  <strong>A terminal</strong>
                  <span>
                    Terminal on macOS/Linux, Git Bash or WSL on Windows
                  </span>
                </div>
              </div>

              <h3 className="om-mcp-category">The 4 steps</h3>
              <ol className="om-mcp-flow om-mcp-roadmap">
                <li className="om-mcp-flow-node">
                  <span className="om-mcp-mini-label">Step 1</span>
                  <strong>Create a OneMap Account</strong>
                  <span>Register for a OneMap account to access MCP services.</span>
                </li>
                <li className="om-mcp-flow-node">
                  <span className="om-mcp-mini-label">Step 2</span>
                  <strong>Obtain a Refresh token</strong>
                  <span>Generate a refresh token to securely authenticate and maintain access to OneMap services.</span>
                  <span className="om-mcp-step-meta">Token validity: 30 days</span>
                </li>
                <li className="om-mcp-flow-node">
                  <span className="om-mcp-mini-label">Step 3</span>
                  <strong>Generate Access token</strong>
                  <span>Exchange the refresh token for an access token to authenticate API and MCP requests.</span>
                  <span className="om-mcp-step-meta">Token validity: 24 hours</span>
                </li>
                <li className="om-mcp-flow-node is-highlight">
                  <span className="om-mcp-mini-label">Step 4</span>
                  <strong>Connect to the MCP server</strong>
                  <span>Configure your MCP-enabled AI application with the OneMap MCP server endpoint and access token to start using OneMap tools.</span>
                </li>
              </ol>
            </section>

            {/* ---------- 02 Get your access token ---------- */}
            <section id="mcp-authentication" className="om-mcp-block">
              <h2 className="om-mcp-h2">Get your access token</h2>

              <div className="om-mcp-stack">
                {/* Step 1 */}
                <article className="om-mcp-card om-mcp-card-step">
                  <h3 className="om-mcp-card-title-18">
                    1. Sign up for a OneMap account
                  </h3>
                  <p className="om-mcp-card-body-16">
                    Registering takes three quick actions:
                  </p>
                  <ol className="om-mcp-list">
                    <li>
                      Register with your email and a password at{" "}
                      <ExternalLink href={ONEMAP_REGISTER_URL}>
                        onemap.gov.sg/apidocs/register
                      </ExternalLink>
                      .
                    </li>
                    <li>
                      Check your inbox. OneMap emails a{" "}
                      <strong>confirmation code</strong> to the address you
                      registered with (check your spam folder if it doesn’t show up).
                    </li>
                    <li>
                      Enter that code at{" "}
                      <ExternalLink href={ONEMAP_REGISTER_CONFIRM_URL}>
                        onemap.gov.sg/apidocs/registerconfirm
                      </ExternalLink>{" "}
                      to confirm your registration.
                    </li>
                  </ol>

                  <InsetNote title="Take note of the email and password you signed up with">
                    You'll use them in Step 2. Already have a OneMap
                    account? Skip ahead and use the same email and password.
                  </InsetNote>
                </article>

                {/* Step 2 */}
                <article className="om-mcp-card om-mcp-card-step">
                  <h3 className="om-mcp-card-title-18">
                    2. Generate a refresh token
                  </h3>
                  <p className="om-mcp-card-body-16">
                    Swap your email and password for a{" "}
                    <strong>refresh token</strong>. It’s like a membership card: valid for <strong>30 days</strong> and lets
                    you collect new day passes without typing your password
                    again. Run this, replacing the email and password with
                    yours:
                  </p>
                  <CodeBlock
                    code={REFRESH_CURL}
                    mode="plain"
                    label="refresh token command"
                  />
                  <p className="om-mcp-card-body-16">
                    You'll get back something like this.{" "}
                    <strong>
                      Copy the <mark className="om-mcp-chip">refresh_token</mark> value.
                    </strong>
                  </p>
                  <CodeBlock
                    code={REFRESH_RESPONSE}
                    mode="http"
                    label="refresh token response"
                  />
                </article>

                {/* Step 3 */}
                <article className="om-mcp-card om-mcp-card-step">
                  <h3 className="om-mcp-card-title-18">
                    3. Generate an access token using the refresh token
                  </h3>
                  <p className="om-mcp-card-body-16">
                    Now trade the refresh token for an{" "}
                    <strong>access token</strong>.  This is the day pass your agentic tool shows the server, and it's valid for {" "}
                    <strong>24 hours</strong>. Paste your refresh token from Step 2:
                  </p>
                  <CodeBlock
                    code={ACCESS_CURL}
                    mode="plain"
                    label="access token command"
                  />
                  <p className="om-mcp-card-body-16">
                    Again,{" "}
                    <strong>
                      copy the <mark className="om-mcp-chip">access_token</mark> value.
                    </strong>
                  </p>
                  <CodeBlock
                    code={ACCESS_RESPONSE}
                    mode="http"
                    label="access token response"
                  />

                  <InsetNote title="When the token stops working">
                    After 24 hours, repeat <strong>Step 3 only</strong> to get a
                    new access token. After 30 days, repeat Step 2 as well.
                    Treat both tokens like passwords: don't share them or
                    commit them to Git.
                  </InsetNote>
                </article>
              </div>
            </section>

            {/* ---------- 03 Load the server ---------- */}
            <section id="mcp-connect" className="om-mcp-block">
              <h2 className="om-mcp-h2">Connect to the MCP server</h2>

              <div className="om-mcp-stack">
                <article className="om-mcp-card om-mcp-card-step">
                  <h3 className="om-mcp-card-title-18">
                    4. Use the access token to load the OneMap MCP server
                  </h3>
                  <p className="om-mcp-card-body-16 om-mcp-notice om-mcp-notice-download">
                    Don't have an agentic tool yet? Download{" "}
                    <ExternalLink href={CLAUDE_DESKTOP_URL}>
                      Claude Desktop
                    </ExternalLink>{" "}
                    or <ExternalLink href={KIRO_URL}>Kiro</ExternalLink>.
                  </p>
                  <p className="om-mcp-card-body-16">
                    Your agentic tool needs two things: the server address and
                    your access token. If your tool has a command-line method,
                    start there. Otherwise, skip to{" "}
                    <a
                      href="#mcp-edit-config-file"
                      onClick={(e) => handleAnchorClick(e, "mcp-edit-config-file")}
                    >
                      editing the config file
                    </a>
                    .
                  </p>

                  <div className="om-mcp-note">
                    <h4 className="om-mcp-note-title">
                      Fastest option if you use Claude Code
                    </h4>
                    <p className="om-mcp-card-body-16 om-mcp-notice om-mcp-notice-skip">
                      <strong>
                        Claude Code is a separate command-line tool from
                        Anthropic, not the Claude Desktop app.
                      </strong>{" "}
                      If you only installed Claude Desktop, this shortcut
                      won't work. Skip to{" "}
                      <a
                        href="#mcp-edit-config-file"
                        onClick={(e) => handleAnchorClick(e, "mcp-edit-config-file")}
                      >
                        editing the config file
                      </a>{" "}
                      instead.
                    </p>
                    <p className="om-mcp-card-body-16">
                      <strong>Get an access token in one command.</strong> Once
                      you have a refresh token from Step 2, this prints an
                      access token. Run it in Terminal, Git Bash or WSL.
                    </p>
                    <CodeBlock
                      code={ACCESS_TOKEN_ONE_LINER}
                      mode="plain"
                      label="one-line access token command"
                    />
                    <p className="om-mcp-card-body-16">
                      If{" "}
                      <ExternalLink href={CLAUDE_CODE_URL}>
                        Claude Code
                      </ExternalLink>{" "}
                      is installed, one command adds the server with no file to
                      edit. Paste in the token you just printed.
                    </p>
                    <CodeBlock
                      code={CLAUDE_CODE_ADD}
                      mode="plain"
                      label="Claude Code add server command"
                    />
                    <p className="om-mcp-card-body-16">
                      Access tokens last 24 hours, so you’ll need to repeat both commands once it expires. Refresh by running <mark className="om-mcp-chip">claude mcp remove onemap</mark>{" "}
                      and adding it again with the new token.
                    </p>
                  </div>
                </article>
                <InsetNote>
                  Using Claude Desktop or Kiro instead? Neither currently
                  supports adding a remote server with a custom header from
                  the command line, so copy your access token into the config
                  file below.
                </InsetNote>
              </div>

              {/* ---------- Config file ---------- */}
              <SectionRule >
                Or edit the config file directly
              </SectionRule>

              <p id="mcp-edit-config-file" className="om-mcp-card-body-16">
                Paste the snippet for your tool into its config file and
                replace <mark className="om-mcp-chip">&lt;access_token&gt;</mark> with the token from
                Step 3.
              </p>

              <div className="om-mcp-stack">
                <CodeCard {...SERVER_ADDRESS_CARD} />
              </div>

              <div className="om-mcp-grid-2">
                {CONFIG_CARDS.map((card) => (
                  <CodeCard key={card.id} {...card} />
                ))}
              </div>

              <div className="om-mcp-stack om-mcp-stack-top">
                <article className="om-mcp-card">
                  <h3 className="om-mcp-card-title-18">
                    Restart your tool, then check that all {TOOL_COUNT} OneMap tools are listed:
                  </h3>
                  <ul className="om-mcp-list">
                    <li>
                      <strong>Claude Desktop:</strong>  in newer versions, open the + menu in the chat input and go to <strong>Connectors</strong>; in older versions, click the hammer icon in the chat input
                    </li>
                    <li>
                      <strong>Kiro:</strong>  MCP Servers panel, or run {" "}
                      <mark className="om-mcp-chip">/mcp</mark> in a chat session
                    </li>
                  </ul>
                </article>

                <InsetNote title="Try it">
                  Ask:{" "}
                  <em>
                    &ldquo;Find the address and nearest MRT for 1 North Buona
                    Vista Drive.&rdquo;
                  </em>{" "}
                  Your tool picks the right OneMap tools on its own and answers
                  with live data. If it works, you're done.
                </InsetNote>
              </div>
              <SectionRule id="mcp-option-b">
                Building your own agent?
              </SectionRule>

              <div className="om-mcp-stack">
                <article className="om-mcp-card">
                  <button
                    type="button"
                    className="om-mcp-disclosure"
                    onClick={() => setShowCurlCheck(!showCurlCheck)}
                    aria-expanded={showCurlCheck}
                    aria-controls="mcp-curl-check"
                  >
                    Check the server with curl
                    <Chevron open={showCurlCheck} />
                  </button>

                  <div id="mcp-curl-check" hidden={!showCurlCheck}>
                    <p className="om-mcp-card-body-16">
                      This asks the server for its tool list. A valid token
                      returns all {TOOL_COUNT} tools.
                    </p>
                    <CodeBlock
                      code={TOOLS_LIST_CURL}
                      mode="plain"
                      label="tools/list command"
                    />
                  </div>
                </article>
              </div>
            </section>

            <section id="mcp-tools" className="om-mcp-block">
              <h2 className="om-mcp-h2">Available tools</h2>

              <div className="om-mcp-stack">
                <article className="om-mcp-card">
                  <h3 className="om-mcp-card-title">Tool design notes</h3>
                  <p className="om-mcp-card-body">
                    All coordinate inputs use WGS84 decimal degrees unless the
                    tool name says otherwise (
                    <mark className="om-mcp-chip">XY</mark>, or{" "}
                    <mark className="om-mcp-chip">3414</mark> /{" "}
                    <mark className="om-mcp-chip">3857</mark> for the conversion
                    tools). Population tools take a planning-area <em>name</em>,
                    not an address, so resolve locations with{" "}
                    <mark className="om-mcp-chip">popquery___getPlanningarea</mark>{" "}
                    first. For natural-language agents, prefer{" "}
                    <mark className="om-mcp-chip">address___search</mark>{" "}
                    before routing when the user gives a place name instead of
                    coordinates.
                  </p>
                </article>
              </div>

              {TOOL_GROUPS.map((group) => (
                <React.Fragment key={group.id}>
                  <SectionRule id={group.id}>{group.title}</SectionRule>
                  <div
                    className={
                      isStackedGroup(group)
                        ? "om-mcp-stack om-mcp-stack-top"
                        : "om-mcp-grid-2"
                    }
                  >
                    {group.tools.map((tool) => (
                      <ToolEntry key={tool.name} tool={tool} />
                    ))}
                  </div>
                </React.Fragment>
              ))}
            </section>

            {/* Security and limits */}
            <section id="mcp-security" className="om-mcp-block">
              <h2 className="om-mcp-h2">Security and limits</h2>

              <div className="om-mcp-grid-3">
                <article className="om-mcp-card">
                  <h3 className="om-mcp-card-title-12">Rate limit</h3>
                  <p className="om-mcp-card-body">
                    The MCP gateway enforces{" "}
                    <strong>5 requests per second</strong> per token, with a
                    burst allowance of up to <strong>10 requests</strong>.
                    Requests exceeding the burst limit return  <mark className="om-mcp-chip">429 Too Many
                    Requests</mark>. Add a short backoff and retry in your agent loop.
                  </p>
                </article>

                <article className="om-mcp-card">
                  <h3 className="om-mcp-card-title-12">Credential storage</h3>
                  <p className="om-mcp-card-body">
                    Do not paste access or refresh tokens into shared
                    repositories, prompt libraries, screenshots, or public bug
                    reports. Use secret stores or local environment managers.
                  </p>
                </article>

                <article className="om-mcp-card">
                  <h3 className="om-mcp-card-title-12">Token expiry</h3>
                  <p className="om-mcp-card-body">
                    Access tokens and refresh tokens expire. Check
                  {""} <mark className="om-mcp-chip">expiry_timestamp</mark> and refresh credentials before long-running
                    agent sessions.
                  </p>
                </article>
              </div>
            </section>

            {/* Troubleshooting */}
            <section id="mcp-troubleshooting" className="om-mcp-block">
              <h2 className="om-mcp-h2">Troubleshooting</h2>

              <div className="om-mcp-stack">
                <Troubleshooting rows={TROUBLESHOOTING} />
              </div>
            </section>

            {/* Quick reference */}
            <section id="mcp-quick-reference" className="om-mcp-block">
              <h2 className="om-mcp-h2">Quick reference</h2>

              <div className="om-mcp-stack">
                <QuickReference rows={QUICK_REFERENCE} />
              </div>
            </section>
          </div>
        </div>
      </div>
      <FooterComponent />
    </>
  );
};

export default MCP;